import { Paperclip } from "lucide-react";
import { useEffect, useMemo, useRef } from "react";
import { Link, useLocation } from "react-router";

import { StatusPill, type StatusPillTone } from "nazare-ui/components";

import { MaximisedTablePage } from "../../../../maximised-table-page";
import { useSecondaryHeader } from "../../../../secondary-header-context";
import { InfiniteScrollSentinel, SkeletonRow } from "../../../../skeleton-row";
import { useLoadMoreSentinel } from "../../../../use-load-more-sentinel";
import { useScrollEdges } from "../../../../use-scroll-edges";
import { useDocumentTitle } from "../../../../use-document-title";
import { useWorkflowDefinitions, useWorkflowRuns } from "../../../../workflows-api";
import { WorkflowHistoryFilterControls } from "../workflow-history-filter-controls";
import { RUNS_COLUMN_WIDTHS } from "../runs-columns";
import { TriggerChip } from "./run-detail-status-chip";
import { runElapsedMs, formatDuration } from "./run-detail-format";
import styles from "./runs.module.css";
import { useNow } from "./use-now";
import { normaliseWorkflowStatus, sentenceCaseWorkflowStatus } from "../../../../workflow-step-filter";
import { workflowDisplayLabel } from "../display-labels";
import { useWorkflowQueryState } from "../../../../workflow-query-state";

const PAGE_NAV = [
  { href: "/workflows", label: "Overview" },
  { href: "/workflows/runs", label: "Runs" },
  { href: "/workflows/history", label: "History" },
  { href: "/workflows/definitions", label: "Definitions" },
];

function statusTone(status: string): StatusPillTone {
  status = normaliseWorkflowStatus(status);
  if (["completed", "passed", "success"].includes(status)) return "success";
  if (["failed", "timed_out"].includes(status)) return "error";
  if (["running", "paused", "sleeping", "rerun"].includes(status)) return "info";
  return "neutral";
}

function formatDate(iso: string | null) {
  if (!iso) return "—";
  const date = new Date(iso);
  return Number.isNaN(date.getTime()) ? iso : date.toLocaleString("en-GB", { dateStyle: "medium", timeStyle: "short" });
}

function duration(run: Parameters<typeof runElapsedMs>[0], now: number) {
  const milliseconds = runElapsedMs(run, now);
  return milliseconds === null ? "—" : formatDuration(milliseconds);
}

export function WorkflowRunsPage() {
  useDocumentTitle("Runs");
  const now = useNow();
  const location = useLocation();
  const { filters, setFilters } = useWorkflowQueryState();
  const { workflow, status, period, from, to } = filters;
  const definitions = useWorkflowDefinitions();
  const runsQuery = useWorkflowRuns({ workflowName: workflow, status, period, from, to, limit: 50 });
  const rows = runsQuery.data?.pages.flatMap((page) => page.data) ?? [];
  const emptyPageFetches = useRef(0);
  const filterKey = `${workflow.join("\u0000")}\u0001${status.join("\u0000")}\u0001${period}\u0001${from ?? ""}\u0001${to ?? ""}`;
  useEffect(() => {
    emptyPageFetches.current = 0;
  }, [filterKey]);
  useEffect(() => {
    if (rows.length > 0) {
      emptyPageFetches.current = 0;
      return;
    }
    if (runsQuery.hasNextPage && !runsQuery.isFetchingNextPage && emptyPageFetches.current < 4) {
      emptyPageFetches.current += 1;
      void runsQuery.fetchNextPage();
    }
  }, [runsQuery.fetchNextPage, runsQuery.hasNextPage, runsQuery.isFetchingNextPage, rows.length]);
  const { ref: tableWrapRef, atTop, atBottom } = useScrollEdges<HTMLDivElement>();
  const sentinelRef = useLoadMoreSentinel({
    hasMore: !!runsQuery.hasNextPage,
    isLoadingMore: runsQuery.isFetchingNextPage,
    onLoadMore: () => void runsQuery.fetchNextPage(),
    scrollRootRef: tableWrapRef,
  });
  const secondaryHeader = useMemo(() => (
    <div className={styles.secondaryHeader}>
      <nav className={styles.pageNav} aria-label="Workflow pages">
        {PAGE_NAV.map((item) => (
          <Link
            key={item.href}
            to={`${item.href}${location.search}`}
            className={`${styles.pageNavLink} ${item.href === "/workflows/runs" ? styles.pageNavLinkActive : ""}`}
          >
            {item.label}
          </Link>
        ))}
      </nav>
      <div className={styles.filterControls}>
        <WorkflowHistoryFilterControls
          filters={filters}
          onFiltersChange={setFilters}
          statusMode="raw"
          dateLabel="Filter runs by date"
        />
      </div>
    </div>
  ), [filters, location.search, setFilters]);
  useSecondaryHeader(secondaryHeader);

  return (
    <MaximisedTablePage title="Runs" count={`${rows.length}${runsQuery.hasNextPage ? "+" : ""}`} viewTransitionName="wf-card-runs" minimiseTo={`/workflows${location.search}`} tableWrapRef={tableWrapRef} atTop={atTop} atBottom={atBottom} showLoadingBar={runsQuery.isFetching && !runsQuery.isLoading}>
      <table className={styles.table}>
        <colgroup>
          {RUNS_COLUMN_WIDTHS.map((width, index) => (
            <col key={index} style={{ width }} />
          ))}
        </colgroup>
        <thead><tr><th className={styles.th}>Status</th><th className={styles.th}>Workflow</th><th className={styles.th}>Run ID</th><th className={styles.th}>Created</th><th className={styles.th}>Artefacts</th><th className={`${styles.th} ${styles.thRight}`}>Duration</th></tr></thead>
        <tbody>
          {runsQuery.isLoading ? Array.from({ length: 8 }, (_, index) => <SkeletonRow key={index} columns={6} />) : runsQuery.error ? (
            <tr><td className={styles.td} colSpan={6}>Couldn’t load workflow runs.</td></tr>
          ) : rows.length === 0 && (runsQuery.isFetchingNextPage || (runsQuery.hasNextPage && emptyPageFetches.current < 4)) ? (
            <tr><td className={styles.td} colSpan={6}>Scanning workflow runs…</td></tr>
          ) : rows.length === 0 && runsQuery.hasNextPage ? (
            <tr><td className={styles.td} colSpan={6}>No matching runs found in the first four pages. <button type="button" className={styles.emptyResultAction} onClick={() => { emptyPageFetches.current = 0; void runsQuery.fetchNextPage(); }}>Load more runs</button></td></tr>
          ) : rows.length === 0 ? (
            <tr><td className={styles.td} colSpan={6}>No workflow runs match these filters.</td></tr>
          ) : <>
            {rows.map((run) => (
              <tr key={run.runId} className={styles.row}>
                <td className={styles.td}><Link to={`/workflows/runs/${encodeURIComponent(run.runId)}`} className={styles.rowLink} aria-label={`View run ${run.runId}`} /><StatusPill tone={statusTone(run.status)}>{sentenceCaseWorkflowStatus(run.status)}</StatusPill></td>
                <td className={styles.td}>
                  <span className={styles.workflowCell}>
                    <span className={styles.workflowName}>{workflowDisplayLabel(run, definitions.data?.definitions)}</span>
                    {run.label && <span className={styles.workflowSecondary} title={run.label}>{run.label}</span>}
                    <TriggerChip trigger={run.triggeredBy} />
                  </span>
                </td>
                <td className={`${styles.td} ${styles.runId}`}>{run.runId}</td>
                <td className={`${styles.td} ${styles.created}`}>{formatDate(run.createdAt)}</td>
                <td className={styles.td}><span className={styles.artefacts}><Paperclip className={styles.artefactsIcon} aria-hidden="true" />{run.artifactCount ?? "—"}</span></td>
                <td className={`${styles.td} ${styles.duration}`}>{duration(run, now)}</td>
              </tr>
            ))}
            {runsQuery.hasNextPage && <InfiniteScrollSentinel sentinelRef={sentinelRef} columns={6} />}
            {runsQuery.isFetchingNextPage && Array.from({ length: 3 }, (_, index) => <SkeletonRow key={`more-${index}`} columns={6} />)}
          </>}
        </tbody>
      </table>
    </MaximisedTablePage>
  );
}
